/**
 * ATL-1445 stage 3, P8 (review 1, S-3, decided: QUARANTINE) — v1 absolute stock mirrors, kept OUTSIDE
 * the outbox v2 drain queue.
 *
 * WHAT. A v1 entry whose request sets an ABSOLUTE stock level (`isAbsoluteStockRequest`:
 * `POST /products/stock/bulk`, `PATCH /products/:id/stock`) that reaches outbox v2's side at the flip —
 * copied by the v1 → v2 migration, or converted from B1's 409 — is never replayed: re-asserting a stock
 * level onto the ledger after the fact is the ATL-707 class in reverse (design §2). Holding it at the
 * head of the drain queue (P3's on-sight hold) would add nothing but a stop: every write behind it on
 * that device would wait for an operator, and a pre-flip session sends that mirror after a product edit,
 * a new product's initial stock and a manual stock set. So it goes here instead: its own persisted store
 * (`openQuarantineStore`, the same engine in its own directory / IndexedDB database), which nothing
 * drains. The queue never blocks on it.
 *
 * WHAT IS KEPT. The full v2 entry (`state: 'held'`, `lastError.code: 'absolute-replay'`, origin
 * `v1-migrated` / `mirror-409-converted`, the request body with the stock levels, `enqueuedAt`,
 * `heldSince`), so an operator can upload it for a manual repair of the books or discard it. Each one is
 * paged once, when it is quarantined (`outbox_v2_quarantined`, Sentry `['outbox-v2-quarantined', label]`).
 *
 * WHAT RESOLVES IT. Only an operator: `discardQuarantined` (audited, one event per decision, like P3's
 * `resolveOperatorHold`). There is no `release`: these levels are never sent. The upload for repair is
 * the operator surface's job (§16 "Operator surface"); `listQuarantined` gives it everything it needs.
 * Nothing automatic ever removes an entry from here.
 */
import * as Sentry from '@sentry/react-native'

import { captureOperationalEvent } from '../analytics/operationalTelemetry'
import type { OutboxAlertSink } from './outboxAlerts'
import { adoptionAuditProblem, type NewOutboxEntry, type OutboxAdoptionAudit, type OutboxEntryV2 } from './outboxEntry'
import type { OutboxSlot, OutboxStore } from './outboxStore'

/** The app's quarantine store, lazily (a platform engine that fails to load is a store that cannot be opened). */
export function openAppQuarantineStore(): OutboxStore {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { openQuarantineStore } = require('./store') as { openQuarantineStore: () => OutboxStore }
  return openQuarantineStore()
}

export interface QuarantinedAlert {
  label: string
  origin: NewOutboxEntry['origin']
  entryId: string
  documentId: string
  /** ms since the write was made. */
  ageMs: number
}

export function quarantinedAlert(entry: NewOutboxEntry, now: number): QuarantinedAlert {
  return { label: entry.label, origin: entry.origin, entryId: entry.entryId, documentId: entry.documentId, ageMs: Math.max(0, now - entry.enqueuedAt) }
}

/**
 * Page: an absolute stock level was quarantined. Not a stop (the queue continues), but a person must
 * decide it, and the books may differ from what the device had until they do. Counts and kinds to
 * PostHog; ids to the console and the Sentry context.
 */
export function reportQuarantined(alert: QuarantinedAlert): void {
  const i = alert.label.indexOf('.')
  const module = i === -1 ? alert.label : alert.label.slice(0, i)
  const op = i === -1 ? '' : alert.label.slice(i + 1)
  // eslint-disable-next-line no-console
  console.error(`[outboxV2] QUARANTINED ${alert.label} (absolute stock level, never replayed) — an operator must upload or discard it`, JSON.stringify(alert))
  Sentry.captureException(new Error(`outbox v2 quarantined ${alert.label} (absolute-replay)`), {
    level: 'error',
    fingerprint: ['outbox-v2-quarantined', alert.label],
    tags: { outbox_v2_quarantined: true, outbox_v2_origin: alert.origin, dual_write: alert.label, dual_write_module: module, dual_write_op: op },
    contexts: { outbox_v2: { ...alert } },
  })
  captureOperationalEvent('outbox_v2_quarantined', { label: alert.label, module, op, origin: alert.origin, age_ms: alert.ageMs })
}

/** Everything quarantined for this organization, unreadable records included (for the operator surface). */
export async function listQuarantined(openStore: () => OutboxStore, orgFirestoreId: string): Promise<OutboxSlot[]> {
  return openStore().list(orgFirestoreId)
}

export class QuarantineDecisionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'QuarantineDecisionError'
  }
}

/**
 * OPERATOR-ONLY, AUDITED: discard a quarantined entry (after it was uploaded for repair, or judged
 * moot). One audit event per decision; the entry itself does not survive.
 */
export async function discardQuarantined(options: {
  openStore: () => OutboxStore
  orgFirestoreId: string
  entryId: string
  audit: OutboxAdoptionAudit
  sink: Pick<OutboxAlertSink, 'operatorResolved'>
}): Promise<OutboxEntryV2> {
  const problem = adoptionAuditProblem(options.audit)
  if (problem) throw new QuarantineDecisionError(problem)
  const store = options.openStore()
  const entry = await store.get(options.orgFirestoreId, options.entryId)
  if (!entry) throw new QuarantineDecisionError(`entry ${options.entryId} is not quarantined`)
  await store.remove(options.orgFirestoreId, options.entryId)
  options.sink.operatorResolved({
    entryId: entry.entryId,
    label: entry.label,
    decision: 'discard',
    actor: options.audit.actor,
    reason: options.audit.reason,
  })
  return entry
}
